/**
 * L'écran d'activité : une page entière, pas un encart.
 *
 * C'est là que le joueur passe son temps : l'arène occupe le haut, la
 * progression et le butin en cours défilent dessous. Tout ce qui n'est pas
 * l'action a été déplacé dans les autres écrans.
 */
import type { Ctx } from "../contexte.ts"
import type { Personnage } from "../../sim/etat.ts"
import type { Contenu } from "../../sim/contenu.ts"
import { esc, nb, jauge, vert, orJauge, rougeJauge } from "../fmt.ts"
import { cleClasse } from "../sprites.ts"
import { icone, ic, fondMonstre } from "../sprites.ts"
import { rendreJournal, rendreJournalEquipe } from "../journal.ts"
import { famille, monstre, item, recolte } from "../../sim/contenu.ts"
import { verdictFamille, xpRequise, palierDe, statsPerso } from "../../sim/combat.ts"
import { effetsDofus } from "../../sim/dofus.ts"
import { tempsRecolte, niveauMetier, xpMetier, xpRequiseMetier } from "../../sim/recolte.ts"
import { estConsommable, soinBrut, DELAI_REPAS_MS } from "../../sim/objets.ts"
import { terrainMetier, bandeResistances } from "./monde.ts"
import { pseudoLocal } from "../../net/compte.ts"
import { lienCarte } from "../../net/carte.ts"

/**
 * Aperçu de l'activité, pour le grand écran.
 *
 * Sur mobile on n'a la place que d'un écran à la fois, mais sur un ordinateur
 * laisser le combat hors de vue dès qu'on ouvre son sac est un gâchis : c'est
 * précisément ce qu'on regarde. L'aperçu reprend **les mêmes identifiants** que
 * l'arène complète — `#arene`, `#a-joueur`, `#a-mob`, les deux jauges — si bien
 * que l'animation de combat continue de tourner sans une ligne de code en
 * double. Les deux ne coexistent jamais : l'onglet Jeu affiche l'arène entière.
 */
/**
 * Les autres personnages, sous l'aperçu.
 *
 * Ils travaillent en même temps que l'actif : ne montrer que celui-ci laissait
 * croire qu'eux ne faisaient rien. Une ligne chacun suffit — ce qu'ils font et
 * ce qui leur reste de vie — et la ligne mène à eux.
 */
/** Ce que fait un personnage, en trois mots, pour les listes d'équipe. */
export function libelleActivite(x: Personnage, contenu: Contenu): string {
  if (x.arrete) return "Tombé"
  if (x.activite?.type === "recolte") {
    return item(contenu, recolte(contenu, x.activite.ressourceId)?.item ?? 0)?.nom ?? "Récolte"
  }
  if (x.activite?.type === "combat") return famille(contenu, x.activite.familleId)?.nom ?? "Combat"
  return "Au repos"
}

function equipe(c: Ctx): string {
  const { contenu, etat, p } = c
  const autres = etat.personnages.filter((x) => x.id !== p?.id)
  if (autres.length === 0) return ""
  return `<div class="apercuEquipe">${autres.map((x) => {
    const st = statsPerso(x, contenu, effetsDofus(etat))
    const part = Math.max(0, Math.min(1, x.pv / st.pvMax))
    const quoi = libelleActivite(x, contenu)
    return `<button class="coequipier${x.arrete ? " ko" : ""}" data-perso="${x.id}">
      <span class="tete" style="background-image:url(/img/combat/c${x.classe}-0.webp)"></span>
      <span class="txt">
        <span class="nom">${esc(x.nom)} <em data-nivco="${x.id}">niv ${x.niveau}</em></span>
        <span class="quoi">${esc(quoi)}</span>
      </span>
      <span class="pvmini">
        <b data-pvco="${x.id}">${nb(Math.max(0, Math.round(x.pv)))}</b>
        ${jauge(part, part < .35 ? rougeJauge : vert, `co-${x.id}`)}
      </span>
    </button>`
  }).join("")}</div>`
}

export function apercuActivite(c: Ctx): string {
  const { contenu, p, s } = c
  if (!p || !s) return ""
  // Sans activité, l'aperçu n'a pas d'arène à montrer — mais l'équipe, si.
  if (!p.activite) return equipe(c) ? `<aside class="apercu">${equipe(c)}</aside>` : ""

  const v = p.activite.type === "combat"
    ? verdictFamille(s, contenu, p.activite.familleId, p.pv, p.niveau)
    : null
  return `
  <aside class="apercu">
    ${areneDe(c, p)}
    <!-- Identifiants propres à l'apercu : l'ecran Personnage affiche deja ses
         victoires sous v-kills, et deux elements partageant un identifiant ne
         se reecrivent jamais tous les deux. -->
    <div class="apercuPied">
      <span><b id="v-killsapercu">${nb(p.kills)}</b> victoires</span>
      <b id="v-dps">${v ? nb(v.dpsInfliges) : "—"}</b>
    </div>
    <div class="jlignes apercuJournal" id="journal">${rendreJournal()}</div>
    ${equipe(c)}
  </aside>`
}

/**
 * Les vivres, à portée de main pendant l'activité.
 *
 * Le personnage mange tout seul sous la moitié de sa vie, mais c'est lui qui
 * décide du moment. Ce bandeau sert à reprendre l'avantage quand on le veut, et
 * surtout à voir ce qu'on a en réserve : sans vivres, un farm s'arrête.
 */
function vivres(c: Ctx): string {
  const { contenu, etat, p, s } = c
  if (!p || !s) return ""
  const stock = Object.entries(etat.inventaire)
    .map(([id, n]) => ({ it: item(contenu, Number(id))!, n }))
    .filter((x) => x.it && estConsommable(x.it))
    .sort((a, b) => soinBrut(a.it) - soinBrut(b.it))
  const pleine = p.pv >= s.pvMax
  // Le délai entre deux repas : il fait toute la difficulté du farm au-dessus
  // de son niveau, il doit donc se voir, pas se deviner à un clic sans effet.
  const attente = Math.max(0, p.repasDans ?? 0)

  if (stock.length === 0) {
    return `<section class="carte vivres">
      <h2>Vivres</h2>
      <p class="aide">Tu n'as rien à manger. Les points de vie perdus ne reviennent pas tout seuls :
      sans pain ni potion, le farm s'arrêtera à la première barre vide. Le pain se fabrique avec du blé et de l'eau.</p>
    </section>`
  }
  return `<section class="carte vivres">
    <h2>Vivres <em id="v-repas">${attente > 0
      ? `prêt dans ${Math.ceil(attente / 1000)} s`
      : `${stock.reduce((a, x) => a + x.n, 0)} en réserve`}</em></h2>
    <div class="grille">${stock.map(({ it, n }) => `
      <button class="slot plein" data-manger="${it.id}" ${pleine || attente > 0 ? "disabled" : ""}
        title="${esc(it.nom)} · rend ${nb(Math.round(soinBrut(it) * s.soin))} PV">
        <span class="ic" style="${icone("items", it.id, 40)}"></span>
        <span class="qte" data-stock="${it.id}">${nb(n)}</span>
        <span class="pf haut">+${nb(Math.round(soinBrut(it) * s.soin))}</span>
      </button>`).join("")}</div>
    <p class="aide">${pleine
      ? "Ta vie est pleine : manger maintenant gâcherait le vivre."
      : `Ton personnage mange tout seul en dessous de la moitié de sa vie, au plus une fois toutes les ${Math.round(DELAI_REPAS_MS / 1000)} secondes. Un vivre rend ce qu'il annonce — monte ton Soin, ou cuisine mieux.`}</p>
  </section>`
}

/**
 * L'arène d'**un** personnage, identifiants compris.
 *
 * Chaque panneau porte les siens — `a-joueur-<id>`, `a-pvm-<id>` — parce que
 * l'écran en affiche désormais plusieurs à la fois : des identifiants uniques
 * auraient fait animer le même combat pour toute l'équipe.
 */
function areneDe(c: Ctx, x: Personnage): string {
  const { contenu } = c
  // **Avec les effets de compte.** L'arène calculait ses chiffres sans eux,
  // quand la simulation, elle, les applique : le +50 % de dégâts du Dofus
  // Pourpre agissait en combat sans jamais apparaître à l'écran. « J'ai gardé
  // les mêmes dps sur tous mes persos malgré le nouveau bonus » — Balbouz,
  // 28/08/2026. Même faute que pour la Prospection du Dofus Ivoire trois jours
  // plus tôt : deux chemins de calcul, un seul au courant.
  const s = statsPerso(x, contenu, effetsDofus(c.etat))
  const i = x.id

  if (x.activite?.type === "recolte") {
    const r = recolte(contenu, x.activite.ressourceId)
    const it = r ? item(contenu, r.item) : null
    const niv = r ? niveauMetier(x, r.metier) : 1
    return `
    <div class="arene recolte" id="arene-${i}" style="background-image:url(/img/terrain/${terrainMetier(r?.metier ?? 0)}.webp)">
      <div class="areneEntete">
        <span class="lieu">${esc(r ? contenu.metiers[String(r.metier)]?.nom ?? "" : "")}</span>
        <span class="rythme">${r ? tempsRecolte(r, niv).toFixed(1) : "—"} s</span>
      </div>
      <div class="duel recolteDuel">
        <div class="combattant joueur">
          <div class="perso" id="a-joueur-${i}" style="background-image:url(/img/combat/c${x.classe}-0.webp)"></div>
        </div>
        <div class="combattant adverse">
          <div class="ressource" id="a-res-${i}" style="${it ? icone("items", it.id, 96) : ""}"></div>
        </div>
      </div>
      <div class="volants" id="a-volants-${i}"></div>
      ${r ? `<div class="piedMetier">
        <span>${esc(contenu.metiers[String(r.metier)]?.nom ?? "")} <b id="v-nivmetier-${i}">${niv}</b></span>
        <span class="xpm" id="v-xpmetier-${i}">${nb(xpMetier(x, r.metier))} / ${nb(xpRequiseMetier(niv))}</span>
        <b class="stock" id="v-stock-${i}">${nb(c.etat.inventaire[r.item] ?? 0)}</b>
      </div>
      ${jauge(xpMetier(x, r.metier) / xpRequiseMetier(niv), orJauge, `j-metier-${i}`)}` : ""}
    </div>`
  }

  if (x.activite?.type === "donjon" && x.descente) {
    const en = x.descente
    const d = contenu.donjons[String(en.donjon)]
    const m = en.restants[0] !== undefined ? monstre(contenu, en.restants[0]) : null
    const boss = d ? en.salle >= d.salles : false
    const part = en.pvMonstreMax > 0 ? en.pvMonstre / en.pvMonstreMax : 0
    return `
    <div class="arene donjonArene ${boss ? "salleBoss" : ""}" id="arene-${i}">
      <div class="areneEntete">
        <span class="lieu">${boss ? "👑 Salle du boss" : `Salle ${en.salle} sur ${d?.salles ?? "?"}`}</span>
        <span class="rythme">${esc(d?.nom ?? "")}</span>
      </div>
      <div class="duel">
        <div class="combattant joueur">
          <div class="vie"><span class="lb"><span class="nm">${esc(x.nom)}</span>
            <b id="v-pvj-${i}">${nb(x.pv)} / ${nb(s.pvMax)}</b></span>${jauge(x.pv / s.pvMax, vert, `a-pvj-${i}`)}</div>
          <div class="perso" id="a-joueur-${i}" style="background-image:url(/img/combat/c${x.classe}-0.webp)"></div>
        </div>
        <div class="combattant adverse">
          <div class="vie"><span class="lb"><span class="nm" id="v-nommob-${i}">${m ? esc(m.nom) : "—"}</span>
            <b id="v-pvm-${i}">${nb(Math.max(0, Math.round(en.pvMonstre)))} / ${nb(Math.round(en.pvMonstreMax))}</b></span>
            ${jauge(part, part < .3 ? rougeJauge : vert, `a-pvm-${i}`)}</div>
          <div class="perso mob ${boss ? "grosMob" : ""}" id="a-mob-${i}" style="${m ? fondMonstre(m.id) : ""}"></div>
        </div>
      </div>
      <div class="volants" id="a-volants-${i}"></div>
      <div class="pions">${Array.from({ length: d?.salles ?? 0 }, (_, k) => {
        const n = k + 1
        const dernier = n === (d?.salles ?? 0)
        return `<span class="pion ${n < en.salle ? "faite" : ""} ${n === en.salle ? "encours" : ""} ${dernier ? "boss" : ""}"
          >${dernier ? "👑" : n}</span>`
      }).join("")}</div>
      <p class="restants" id="v-restants-${i}"></p>
    </div>`
  }

  const f = x.activite?.type === "combat" ? famille(contenu, x.activite.familleId) : null
  if (!f) {
    return `<div class="arene vide" id="arene-${i}" style="background-image:var(--terrain)">
      <div class="voile"><p>${esc(x.nom)} attend tes ordres.</p></div>
    </div>`
  }
  const cible = x.cible !== null ? monstre(contenu, x.cible) : null
  const v = verdictFamille(s, contenu, f.id, x.pv, x.niveau)
  return `
  <div class="arene" id="arene-${i}">
    <div class="areneEntete">
      <span class="lieu">${esc(f.nom)}</span>
      <span class="rythme">${v ? nb(v.dpsInfliges) : "—"} ${ic("dommages", 16)}/s</span>
    </div>
    <div class="duel">
      <div class="combattant joueur">
        <div class="vie"><span class="lb"><span class="nm">${esc(x.nom)}</span>
          <b id="v-pvj-${i}">${nb(x.pv)} / ${nb(s.pvMax)}</b></span>${jauge(x.pv / s.pvMax, vert, `a-pvj-${i}`)}</div>
        <div class="perso" id="a-joueur-${i}" style="background-image:url(/img/combat/c${x.classe}-0.webp)"></div>
      </div>
      <div class="combattant adverse">
        <div class="vie"><span class="lb"><span class="nm" id="v-nommob-${i}">${cible ? esc(cible.nom) : "—"}</span>
          <b id="v-pvm-${i}">${cible ? nb(palierDe(cible).pv) : "—"}</b></span>${jauge(1, vert, `a-pvm-${i}`)}</div>
        <div class="perso mob" id="a-mob-${i}" style="${cible ? fondMonstre(cible.id) : ""}"></div>
      </div>
    </div>
    <div class="volants" id="a-volants-${i}"></div>
    ${x.arrete ? `<div class="voile"><p>${esc(x.nom)} est tombé.</p>
      <button class="primaire mini" data-relancer="${i}">Reprendre</button></div>` : ""}
  </div>`
}

/**
 * L'invitation à partager, en pied de l'écran de jeu.
 *
 * Le partage existait, replié dans Perso ▸ Compte, là où on ne va qu'une fois —
 * pour sécuriser sa partie — et jamais plus. Or ce qu'on a envie de montrer,
 * c'est ce qu'on vient d'accomplir, et c'est ici qu'on le voit.
 *
 * On montre **sa propre carte**, pas une explication : le joueur découvre ce
 * que verront les autres, et c'est cette image qui donne envie de la coller
 * quelque part. Elle est horodatée pour ne pas rester bloquée sur une version
 * périmée, et s'efface d'elle-même si elle n'existe pas encore — le compte a pu
 * être créé il y a dix secondes.
 *
 * Rien pour qui n'a pas de pseudonyme : il n'a pas de page publique, et la
 * bannière de sécurisation le lui dit déjà, plus haut.
 */
function partager(c: Ctx): string {
  const pseudo = pseudoLocal()
  if (!pseudo) return ""
  // **Les chiffres du miroir, ou aucun chiffre.** Les annoncer depuis l'état
  // local ferait dire au message ce que l'image dément — c'est la contradiction
  // qu'on a déjà eu à corriger sur la carte elle-même (§18).
  const ch = c.chiffres
  // On ne se vante pas de zéro : un compte tout neuf partage son équipe, pas
  // son tableau de chasse vide.
  const texte = ch && ch.kills > 0
    ? `Mon équipe sur Dofidle : niveau ${ch.niveau}, ${nb(ch.kills)} victoires.`
    : "Mon équipe sur Dofidle."
  const lien = `${location.origin}/j/${encodeURIComponent(pseudo)}`
  const x = `https://x.com/intent/post?text=${encodeURIComponent(texte)}&url=${encodeURIComponent(lien)}`
  return `
  <section class="carte partageJeu">
    <h2>Montre ton équipe</h2>
    <div class="corpsPartage">
      <a class="apercuCarte" href="${esc(lien)}" target="_blank" rel="noopener" title="Voir ma page publique">
        <img src="${esc(lienCarte(pseudo))}" alt="La carte de partage de ${esc(pseudo)}"
          loading="lazy" onerror="this.closest('.apercuCarte')?.remove()" />
      </a>
      <div class="txtPartage">
        <p class="aide">Ta page publique affiche ton niveau, tes succès et tes personnages.
          Collé sur X, Discord ou WhatsApp, le lien s'ouvre sur cette image.</p>
        <div class="actions">
          <a class="mini marqueX" href="${esc(x)}" target="_blank" rel="noopener" id="partagerX">Partager sur X</a>
          <button id="partager">${c.partage.envoi ? "Préparation…" : "Copier le lien"}</button>
          <a class="mini" href="${esc(lien)}" target="_blank" rel="noopener">Voir ma page</a>
        </div>
        ${c.partage.message ? `<p class="verdict ${c.partage.erreur ? "ko" : "ok"}">${esc(c.partage.message)}</p>` : ""}
      </div>
    </div>
  </section>`
}

/**
 * L'écran d'activité : **toute l'équipe**, pas seulement le personnage actif.
 *
 * On n'y voyait que l'actif, et les autres travaillaient hors de vue — sur un
 * jeu dont le cœur est de faire tourner plusieurs personnages à la fois, c'était
 * en cacher l'essentiel. Deux panneaux par ligne dès qu'il y a la place.
 */
export function vueJeu(c: Ctx): string {
  const { contenu, etat, p, s } = c
  if (!p || !s) return ""

  const equipe = etat.personnages
  return `
  <div class="persos">${equipe.map((x) => {
    const actif = x.id === p.id
    return `<section class="carte plein perso-panneau ${actif ? "actif" : ""}">
      <button class="tetePerso" data-perso="${x.id}" title="${actif ? "Personnage affiché" : `Passer sur ${esc(x.nom)}`}">
        <span class="tete" style="${icone("classes", cleClasse(x.classe), 32)}"></span>
        <span class="txt">
          <span class="nom">${esc(x.nom)} <em id="v-niv-${x.id}">niv ${x.niveau}</em></span>
          <span class="det">${esc(libelleActivite(x, contenu))}</span>
        </span>
        ${actif ? `<span class="marque">affiché</span>` : ""}
      </button>
      ${areneDe(c, x)}
      <div class="progression">
        ${(() => {
          // **Les dégâts, sur le panneau de chaque personnage.**
          //
          // L'écran Jeu n'en montrait aucun : on y voyait la vie monter et
          // descendre, jamais la force de frappe. Un joueur à six personnages
          // n'avait donc aucun moyen de constater l'effet d'un équipement ou
          // d'un Dofus sans ouvrir la fiche de chacun, l'un après l'autre.
          // « J'ai gardé les mêmes dps sur tous mes persos malgré le nouveau
          // bonus » — Balbouz, 28/08/2026, qui ne voyait rien parce qu'il n'y
          // avait rien à voir.
          if (x.activite?.type !== "combat") return ""
          const v = verdictFamille(statsPerso(x, contenu, effetsDofus(etat)),
            contenu, x.activite.familleId, x.pv, x.niveau)
          return v ? `<div class="ligneXp">
            <span>Dégâts par seconde</span>
            <b id="v-dpsp-${x.id}">${nb(v.dpsInfliges)}</b>
          </div>` : ""
        })()}
        <div class="ligneXp">
          <span>Expérience</span>
          <b id="v-xp-${x.id}">${nb(x.xp)} / ${nb(xpRequise(x.niveau))}</b>
        </div>
        ${jauge(x.xp / xpRequise(x.niveau), orJauge, `j-xp-${x.id}`)}
      </div>
      <div class="actions">
        ${x.activite
          ? `<button class="mini" data-arreter="${x.id}">Arrêter</button>`
          : `<button class="mini primaire" data-choisiractivite="${x.id}">Choisir une activité</button>`}
      </div>
    </section>`
  }).join("")}${partager(c)}</div>

  ${p.activite?.type === "combat" && famille(contenu, p.activite.familleId)
    ? `<section class="carte">
        <div class="resline">
          <span class="lbl">Résistances de la famille de ${esc(p.nom)}</span>
          ${bandeResistances(c, p.activite.familleId)}
        </div>
      </section>`
    : ""}

  ${vivres(c)}

  <section class="carte journal">
    <h2>Journal ${etat.personnages.length > 1 ? `<em>de l'équipe</em>` : `de ${esc(p.nom)}`}</h2>
    <div class="jlignes" id="journal">${etat.personnages.length > 1
      ? rendreJournalEquipe(etat.personnages)
      : rendreJournal()}</div>
  </section>`
}



export { vert, rougeJauge }
